import { useState } from "react";
import type { AskUserQuestion, AskUserQuestionAnswer, AskUserQuestionOption, AskUserQuestionStatus } from "../../api/types.js";

function compactText(value: string) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function answeredText(answer: AskUserQuestionAnswer) {
  return answer.values.join("、");
}

export function formatAskUserQuestionAnswersForUser(answers: AskUserQuestionAnswer[]) {
  const lines = ["我已回答以下澄清问题："];
  for (const answer of answers) {
    if (answer.values.length) lines.push(`- ${answer.header}：${answeredText(answer)}`);
  }
  return lines.join("\n");
}

export function AskUserQuestionCard({
  ask,
  status,
  answers = [],
  disabled = false,
  isFinalStep = true,
  onSubmit,
  onCancel
}: {
  ask: AskUserQuestion;
  status: AskUserQuestionStatus;
  answers?: AskUserQuestionAnswer[];
  disabled?: boolean;
  isFinalStep?: boolean;
  onSubmit?: (answers: AskUserQuestionAnswer[]) => void;
  onCancel?: () => void;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [other, setOther] = useState<Record<string, string>>({});
  const [error, setError] = useState("");

  if (status === "submitted") {
    return (
      <section className="ask-question-submitted" data-ask-user-question-card data-ask-status="submitted">
        <div className="ask-question-tool-label"><span /><strong>AskUserQuestion</strong></div>
        <ul>
          {answers.filter((answer) => answer.values.length).map((answer) => (
            <li key={answer.questionId}>
              <strong>{answer.header}</strong>
              <span>{answeredText(answer)}</span>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (status === "cancelled") {
    return (
      <section className="ask-question-interrupted" data-ask-user-question-card data-ask-status="cancelled">
        <div className="ask-question-tool-label"><span /><strong>AskUserQuestion</strong></div>
        <div className="ask-question-tool-output">
          <span>OUT</span>
          <p>用户已取消本次澄清问题。</p>
        </div>
        <p className="ask-question-interrupted-copy">已中断</p>
      </section>
    );
  }

  const total = ask.questions.length;
  const index = Math.min(activeIndex, total - 1);
  const activeQuestion = ask.questions[index];
  const isLast = index >= total - 1;
  const selectionMode = activeQuestion.options.length === 0
    ? "填写"
    : activeQuestion.multiSelect ? "可多选" : "单选";

  const hasOtherText = (question: AskUserQuestion["questions"][number]) =>
    Boolean(question.allowOther && compactText(other[question.id] || ""));
  const isAnswered = (question: AskUserQuestion["questions"][number]) =>
    (selected[question.id]?.length || 0) > 0 || hasOtherText(question);

  function buildAnswers(selectedMap: Record<string, string[]>, otherMap: Record<string, string>) {
    return ask.questions.map((question) => {
      const selectedOptions = question.options.filter((option) => (selectedMap[question.id] || []).includes(option.value));
      const otherText = question.allowOther ? compactText(otherMap[question.id] || "") : "";
      const values = [...selectedOptions.map((option) => option.label), ...(otherText ? [otherText] : [])];
      return {
        questionId: question.id,
        header: question.header,
        question: question.question,
        selectedOptions: question.multiSelect ? selectedOptions : selectedOptions.slice(0, 1),
        ...(otherText ? { other: otherText } : {}),
        values: question.multiSelect ? values : values.slice(0, 1)
      };
    });
  }

  function trySubmit(selectedMap: Record<string, string[]> = selected, otherMap: Record<string, string> = other) {
    const nextAnswers = buildAnswers(selectedMap, otherMap);
    const missing = ask.questions.find((question) => question.required && !nextAnswers.find((answer) => answer.questionId === question.id)?.values.length);
    if (missing) {
      setActiveIndex(Math.max(0, ask.questions.findIndex((question) => question.id === missing.id)));
      setError(missing.options.length ? "请先回答当前必填问题，或填写其他。" : "请先填写当前必填问题。");
      return;
    }
    onSubmit?.(nextAnswers);
  }

  function goTo(next: number) {
    setError("");
    setActiveIndex(Math.max(0, Math.min(total - 1, next)));
  }

  function advance() {
    if (activeQuestion.required && !isAnswered(activeQuestion)) {
      setError(activeQuestion.options.length ? "请先回答当前必填问题，或填写其他。" : "请先填写当前必填问题。");
      return;
    }
    if (isLast) trySubmit();
    else goTo(index + 1);
  }

  function skip() {
    setError("");
    if (isLast) trySubmit();
    else goTo(index + 1);
  }

  function chooseSingle(option: AskUserQuestionOption) {
    if (disabled) return;
    const nextSelected = { ...selected, [activeQuestion.id]: [option.value] };
    const nextOther = { ...other, [activeQuestion.id]: "" };
    setError("");
    setSelected(nextSelected);
    setOther(nextOther);
    if (isLast) {
      if (!isFinalStep) trySubmit(nextSelected, nextOther);
    } else {
      goTo(index + 1);
    }
  }

  function toggleMulti(option: AskUserQuestionOption) {
    if (disabled) return;
    setError("");
    setSelected((current) => {
      const values = current[activeQuestion.id] || [];
      return values.includes(option.value)
        ? { ...current, [activeQuestion.id]: values.filter((value) => value !== option.value) }
        : { ...current, [activeQuestion.id]: [...values, option.value] };
    });
  }

  function updateOther(value: string) {
    setError("");
    setOther((current) => ({ ...current, [activeQuestion.id]: value }));
    if (!activeQuestion.multiSelect) setSelected((current) => ({ ...current, [activeQuestion.id]: [] }));
  }

  return (
    <section className="ask-user-question-card" data-ask-user-question-card data-ask-status="pending">
      <header className="ask-user-question-head">
        <div className="ask-user-question-head-main">
          <div className="ask-user-question-context">
            {ask.title && <span className="ask-user-question-eyebrow">{ask.title}</span>}
            <span className="ask-user-question-selection-mode" data-ask-selection-mode>
              {selectionMode}
            </span>
          </div>
          <strong className="ask-user-question-prompt">{activeQuestion.question}</strong>
        </div>
        <div className="ask-user-question-head-tools">
          {total > 1 && (
            <div className="ask-user-question-pager" data-ask-pager>
              <button className="ask-pager-btn" type="button" data-ask-prev aria-label="上一题" disabled={index === 0} onClick={() => goTo(index - 1)}>‹</button>
              <span className="ask-pager-count" data-ask-pager-count>{index + 1} / {total}</span>
              <button className="ask-pager-btn" type="button" data-ask-next-page aria-label="下一题" disabled={isLast} onClick={() => goTo(index + 1)}>›</button>
            </div>
          )}
          <button className="ask-user-question-close" type="button" data-cancel-ask-user-question aria-label="取消" onClick={onCancel}>×</button>
        </div>
      </header>
      {ask.message && index === 0 && <p className="ask-user-question-message">{ask.message}</p>}
      <div className="ask-user-question-options" role={activeQuestion.multiSelect ? "group" : "radiogroup"} aria-label={activeQuestion.question}>
        {activeQuestion.options.map((option, optionIndex) => {
          const checked = (selected[activeQuestion.id] || []).includes(option.value);
          return (
            <button
              className={`ask-option-row ${checked ? "is-selected" : ""}`}
              type="button"
              key={option.id}
              role={activeQuestion.multiSelect ? "checkbox" : "radio"}
              aria-checked={checked}
              data-ask-choice
              disabled={disabled}
              onClick={() => (activeQuestion.multiSelect ? toggleMulti(option) : chooseSingle(option))}
            >
              <span className="ask-option-index">{optionIndex + 1}</span>
              <span className="ask-option-copy">
                <span className="ask-option-label">{option.label}</span>
                {option.description && <span className="ask-option-desc">{option.description}</span>}
              </span>
              <span className="ask-option-arrow" aria-hidden="true">→</span>
            </button>
          );
        })}
        {activeQuestion.allowOther && (
          <div className="ask-option-other">
            {activeQuestion.options.length > 0 && (
              <div className="ask-option-other-head">
                <span className="ask-option-index ask-option-index-other" aria-hidden="true">✎</span>
                <span className="ask-option-label">{activeQuestion.otherLabel || "其他…"}</span>
              </div>
            )}
            <input
              className="ask-option-other-input"
              type="text"
              data-ask-other-input
              {...(activeQuestion.options.length === 0 ? { "data-ask-free-text-input": true } : {})}
              placeholder={activeQuestion.placeholder}
              value={other[activeQuestion.id] || ""}
              maxLength={240}
              autoFocus={activeQuestion.options.length === 0}
              onChange={(event) => updateOther(event.target.value)}
            />
          </div>
        )}
      </div>
      {error && <div className="ask-user-question-error" data-ask-question-error>{error}</div>}
      <div className="ask-user-question-actions">
        {!activeQuestion.required && (
          <button className="ask-skip" type="button" data-ask-skip onClick={skip}>Skip</button>
        )}
        {isLast ? (
          <button className="ask-primary" type="button" data-submit-ask-user-question disabled={disabled} onClick={() => trySubmit()}>
            {ask.submitLabel}
          </button>
        ) : (
          <button className="ask-primary" type="button" data-ask-next disabled={disabled} onClick={advance}>
            下一步
          </button>
        )}
      </div>
    </section>
  );
}
